import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { appStoreCardAnimation } from '../../../utils/animations';

interface AppStoreHeroProps {
  category?: string;
  title: string;
  subtitle?: string;
  description?: string;
  backgroundImage?: string;
  accentColor?: string;
  buttonText?: string;
  buttonLink?: string;
}

export const AppStoreHero: React.FC<AppStoreHeroProps> = ({
  category = "TODAY",
  title = "Featured Collection",
  subtitle = "Handpicked for You",
  description = "Discover our specially curated content that matches your learning goals",
  backgroundImage,
  accentColor = "#007AFF",
  buttonText = "Get Started",
  buttonLink = "#"
}) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.6]);

  return (
    <motion.div
      className="relative overflow-hidden rounded-3xl"
      initial="offscreen"
      whileInView="onscreen"
      viewport={{ once: true, amount: 0.3 }}
      variants={appStoreCardAnimation}
    >
      <div className="appstore-card appstore-card-featured h-[500px]">
        {/* Background with Parallax */}
        <motion.div
          className="absolute inset-0"
          style={{ y }}
        >
          {backgroundImage ? (
            <img
              src={backgroundImage}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full mesh-gradient-bg"
              style={{
                background: `linear-gradient(135deg, ${accentColor}22 0%, ${accentColor}11 50%, transparent 100%)`
              }}
            />
          )}
        </motion.div>

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* Content */}
        <motion.div
          className="relative z-10 h-full flex flex-col justify-end p-8 text-white"
          style={{ opacity }}
        >
          {/* Category Tag */}
          <motion.div
            className="inline-flex items-center mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span
              className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full"
              style={{ backgroundColor: accentColor }}
            >
              {category}
            </span>
          </motion.div>

          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-2 heading-premium">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xl md:text-2xl opacity-90 mb-4">
                {subtitle}
              </p>
            )}
          </motion.div>

          {/* Description */}
          {description && (
            <motion.p
              className="text-lg opacity-80 mb-6 max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {description}
            </motion.p>
          )}

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <motion.button
              className="px-8 py-3 bg-white text-black font-semibold rounded-full hover:shadow-2xl transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = buttonLink}
              style={{
                boxShadow: '0 4px 20px rgba(255, 255, 255, 0.3)'
              }}
            >
              {buttonText}
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Decorative Elements */}
        <motion.div
          className="absolute top-10 right-10 w-32 h-32 rounded-full blur-3xl"
          style={{ backgroundColor: accentColor }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
    </motion.div>
  );
};